import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../utils/api';
import { useSync } from '../../context/SyncContext';
import { useAuth } from '../../context/AuthContext';
import { newCollectionRecord } from '../../utils/collectionRecord';
import { fieldStore } from '../../utils/fieldBatches';
import { normaliseTag } from '../../utils/specimenCode';
import useUnsentBatches, { serverIdsOf } from '../../components/field/useUnsentBatches';

/**
 * The collections table: what the server has, plus what is still queued.
 *
 * THE OPTIMISTIC HALF IS DRAWN FROM THE QUEUE, not from a second copy in this
 * component's state. The queue is what will actually be sent, it survives a
 * reload, and a second list would be one more thing to keep in step with it.
 * A collection saved in a field appears in the table at once, marked
 * `_queuedOnly`, and the mark goes when the row comes back from the server.
 *
 * A `_queuedOnly` row cannot be opened: there is nothing at
 * GET /collections/{id} yet, so the caller suppresses the click rather than
 * letting it produce a 404 and an empty modal.
 *
 * Field Mode and the map keep their records in a store of their own
 * (utils/fieldBatches.js) until the outbox has sent them. Those are listed
 * first, marked `_deviceOnly` and not openable either, as the map shows them
 * as grey pins: the two screens agree on what this person has recorded, sent
 * or not. One leaving the store has reached the server, which is read again.
 * The camera's unfinished specimen (`active`) is not a record yet.
 * `store` defaults to the page's own; tests pass theirs.
 */
const OFFLINE_KEY = 'pitriot_offline_drafts';

/** A batch still on this device, as a row of the table: the map's localPoint
 *  rule for what it is. */
export function deviceRow(batch) {
  const d = batch.details || {};
  return {
    sample_id: `device-${batch.id}`,
    _deviceOnly: true,
    attention: batch.status === 'attention',
    code: batch.specimenCode || normaliseTag(d.label) || null,
    kind: d.observeOnly ? 'observation' : 'collection',
    collection_datetime: batch.startedAt || batch.createdAt || null,
    region: batch.region || null,
    substrate: d.substrate || null,
    current_rank: d.rank || null,
    current_taxon_name: d.taxon_name || null,
    thumbnail_url: batch.thumbnail || null,
    photo_count: (batch.photos || []).filter(p => !p.skipped).length,
    is_draft: null,
  };
}

export const readLegacyDrafts = () => {
  try { return JSON.parse(localStorage.getItem(OFFLINE_KEY) || '[]'); }
  catch { return []; }
};

export const clearLegacyDrafts = () => {
  try { localStorage.removeItem(OFFLINE_KEY); } catch { /* private mode */ }
};

export default function useCollectionList({ store: injectedStore } = {}) {
  const { t } = useTranslation('collections');
  const { user } = useAuth();
  const { enqueue, pendingEntries } = useSync();
  const store = useMemo(() => injectedStore || fieldStore(), [injectedStore]);

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const tRef = useRef(t);
  tRef.current = t;

  // `quiet`: a re-read in the background (a Field Mode record just arrived)
  // keeps the table on screen instead of swapping it for the loader.
  const load = useCallback((quiet = false) => {
    if (quiet !== true) setLoading(true);
    return api.get('/collections')
      .then(res => { setCollections(res.data); setError(''); })
      .catch(err => setError(err.response?.data?.error || tRef.current('failedToLoad')))
      .finally(() => setLoading(false));
  }, []);
  const reread = useCallback(() => load(true), [load]);

  // `load` is stable, so this runs once, and the effects below can call it
  // without listing it as a reason to run again.
  useEffect(() => { load(); }, [load]);

  // Anything the old per-screen draft store still holds moves into the queue,
  // once. Each keeps the time it was SAVED rather than the time of the
  // upgrade: a collection written on Monday must not arrive claiming to be
  // newer than a correction somebody made on Wednesday.
  useEffect(() => {
    const legacy = readLegacyDrafts();
    if (legacy.length === 0) return;
    (async () => {
      for (const draft of legacy) {
        await enqueue('field_collection', newCollectionRecord(draft.payload, {
          collectorId: user?.user_id,
          now: () => draft.savedAt || new Date().toISOString(),
        }));
      }
      clearLegacyDrafts();
    })();
    // Once per mount, whatever the queue or the account does afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The queue just emptied, so what it was holding is now on the server under
  // the server's own view of it. Re-read rather than trusting the optimistic
  // rows, which know only what this device sent.
  const previousPending = useRef(0);
  useEffect(() => {
    const now = pendingEntries.length;
    if (previousPending.current > 0 && now === 0) load();
    previousPending.current = now;
  }, [pendingEntries.length, load]);

  // Field Mode's and the map's records on this device; one sent in full
  // leaves the list and the server is read again. One the outbox has created
  // but not finished is shown from the device, and its half-made server row
  // (a draft, its photos still to come) is left out until then.
  const unsent = useUnsentBatches(store, user?.user_id, reread).filter(b => b.status !== 'active');
  const onDevice = unsent.map(deviceRow);
  const inProgress = serverIdsOf(unsent);

  const queued = pendingEntries.filter(e => e.table === 'field_collection');
  const onServer = new Set(collections.map(c => c.sample_id));
  const rows = [
    ...onDevice,
    ...queued.filter(e => !onServer.has(e.record.sample_id))
      .map(e => ({ ...e.record, _queuedOnly: true })),
    ...collections.filter(c => !inProgress.has(c.sample_id)),
  ];
  const queuedIds = new Set(queued.map(e => e.record.sample_id));

  return { rows, collections, queuedIds, loading, error, setError, reload: load };
}
